import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import { GridColDef } from '@mui/x-data-grid';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';
import { financialsApi, ppmProjectsApi, ProjectFinancialEntry, PpmProjectListItem, FinancialEntryType } from '../api/ppm';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { NexusBarChart } from './charts/NexusCharts';
import { computeFinance, formatCurrencyShort, formatSignedCurrencyShort, formatPercent } from '../utils/finance';
import { tokens } from '../theme/tokens';

const TYPES: Array<{ value: FinancialEntryType; label: string }> = [
  { value: 'COST', label: 'Cost' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'REVENUE', label: 'Revenue' },
];

const FinancialsPage: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'financials');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string }>({ severity: 'success', message: '' });

  const [projects, setProjects] = useState<PpmProjectListItem[]>([]);
  const [rows, setRows] = useState<ProjectFinancialEntry[]>([]);
  const [companyBudget, setCompanyBudget] = useState<number>(0);
  const [topPanel, setTopPanel] = useState<null | 'entries' | 'spend' | 'revenue' | 'projects'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const [filters, setFilters] = useState<{ projectId: string; fiscalYear: string; fiscalQuarter: string; type: string }>({
    projectId: '',
    fiscalYear: '',
    fiscalQuarter: '',
    type: '',
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectFinancialEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    projectId: '',
    type: 'COST' as FinancialEntryType,
    amount: '',
    currency: 'USD',
    fiscalYear: '',
    fiscalQuarter: '',
    transactionDate: new Date().toISOString().slice(0, 10),
    category: '',
    description: '',
  });

  const money = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }), []);
  const moneyFull = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }), []);

  const stats = useMemo(() => {
    const entries = rows.length;
    const spend = rows.reduce((s, r: any) => {
      const t = String(r?.type || '').toUpperCase();
      if (t === 'COST' || t === 'EXPENSE') return s + Number(r?.amount || 0);
      return s;
    }, 0);
    const revenue = rows.reduce((s, r: any) => {
      const t = String(r?.type || '').toUpperCase();
      if (t === 'REVENUE') return s + Number(r?.amount || 0);
      return s;
    }, 0);
    const projectsCount = new Set(rows.map((r: any) => String(r?.projectId || r?.Project?.id || '')).filter(Boolean)).size;
    const latestDate = rows.reduce((max: Date | null, r: any) => {
      const d = r?.transactionDate ? new Date(r.transactionDate) : null;
      if (!d || isNaN(d.getTime())) return max;
      if (!max || d.getTime() > max.getTime()) return d;
      return max;
    }, null as Date | null);
    return { entries, spend, revenue, projectsCount, latestDate };
  }, [rows]);

  // Allocation-based budget model: Budget (ceiling) vs Allocated (commitment = EAC)
  // vs Spent (actuals). ETC = Allocated − Spent, Variance = Budget − EAC.
  const finance = useMemo(() => {
    const allocated = rows.reduce((s, r: any) => {
      return String(r?.type || '').toUpperCase() === 'ALLOCATION' ? s + Number(r?.amount || 0) : s;
    }, 0);
    let budget = 0;
    if (filters.projectId) {
      budget = Number(projects.find((p) => p.id === filters.projectId)?.budgetedCost || 0);
    } else {
      budget = projects.reduce((s, p) => s + Number(p.budgetedCost || 0), 0);
    }
    if (!budget) budget = companyBudget;
    return computeFinance({ budget, allocated, spent: stats.spend });
  }, [rows, projects, filters.projectId, companyBudget, stats.spend]);

  const budgetChartData = useMemo(
    () => [
      { name: 'Budget', value: finance.budget },
      { name: 'Allocated', value: finance.allocated },
      { name: 'Spent', value: finance.spent },
      { name: 'EAC', value: finance.eac },
    ],
    [finance]
  );

  const topProjectsBySpend = useMemo(() => {
    const byProject = new Map<
      string,
      { projectId: string; name: string; spend: number; revenue: number; entries: number; lastDate: string | null }
    >();

    (rows || []).forEach((r: any) => {
      const projectId = String(r?.projectId || r?.Project?.id || '');
      if (!projectId) return;
      const name = String(r?.Project?.name || projectId);
      const key = projectId;
      const cur = byProject.get(key) || { projectId, name, spend: 0, revenue: 0, entries: 0, lastDate: null as string | null };
      const t = String(r?.type || '').toUpperCase();
      const amt = Number(r?.amount || 0);
      if (t === 'COST' || t === 'EXPENSE') cur.spend += amt;
      if (t === 'REVENUE') cur.revenue += amt;
      cur.entries += 1;
      const d = r?.transactionDate ? String(r.transactionDate).slice(0, 10) : null;
      if (d && (!cur.lastDate || d > cur.lastDate)) cur.lastDate = d;
      byProject.set(key, cur);
    });

    return Array.from(byProject.values()).sort((a, b) => b.spend - a.spend);
  }, [rows]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const [projRes, finRes, budgetRes] = await Promise.all([
        ppmProjectsApi.getAll({ source: 'internal' }),
        financialsApi.getAll({
          projectId: filters.projectId || undefined,
          fiscalYear: filters.fiscalYear ? Number(filters.fiscalYear) : undefined,
          fiscalQuarter: filters.fiscalQuarter ? Number(filters.fiscalQuarter) : undefined,
          type: filters.type || undefined,
        }),
        financialsApi.getCompanyBudget().catch(() => ({ data: null } as any)),
      ]);
      setProjects(projRes.data || []);
      setRows(finRes.data || []);
      const b: any = budgetRes?.data || {};
      setCompanyBudget(Number(b?.totalBudget ?? b?.budget ?? b?.companyBudget?.totalBudget ?? 0) || 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load financial entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.projectId, filters.fiscalYear, filters.fiscalQuarter, filters.type]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      projectId: filters.projectId || (projects[0]?.id || ''),
      type: 'COST',
      amount: '',
      currency: 'USD',
      fiscalYear: filters.fiscalYear || `${new Date().getFullYear()}`,
      fiscalQuarter: filters.fiscalQuarter || `${Math.floor(new Date().getMonth() / 3) + 1}`,
      transactionDate: new Date().toISOString().slice(0, 10),
      category: '',
      description: '',
    });
    setEditOpen(true);
  };

  const openEdit = (r: ProjectFinancialEntry) => {
    setEditing(r);
    setForm({
      projectId: r.projectId,
      type: (r.type as any) || 'COST',
      amount: String(r.amount ?? ''),
      currency: (r.currency as any) || 'USD',
      fiscalYear: r.fiscalYear != null ? String(r.fiscalYear) : '',
      fiscalQuarter: r.fiscalQuarter != null ? String(r.fiscalQuarter) : '',
      transactionDate: r.transactionDate ? String(r.transactionDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
      category: (r.category as any) || '',
      description: (r.description as any) || '',
    });
    setEditOpen(true);
  };

  const save = async () => {
    if (!canEdit) return;
    const amount = Number(form.amount);
    if (!form.projectId) return;
    if (!Number.isFinite(amount) || amount <= 0) {
      setToast({ severity: 'error', message: 'Amount must be a positive number' });
      setToastOpen(true);
      return;
    }
    try {
      setSaving(true);
      const payload: any = {
        type: form.type,
        amount,
        currency: form.currency || 'USD',
        category: form.category.trim() ? form.category.trim() : undefined,
        description: form.description.trim() ? form.description.trim() : undefined,
        fiscalYear: form.fiscalYear ? Number(form.fiscalYear) : undefined,
        fiscalQuarter: form.fiscalQuarter ? Number(form.fiscalQuarter) : undefined,
        transactionDate: new Date(`${form.transactionDate}T00:00:00.000Z`),
        Project: { connect: { id: form.projectId } },
      };
      if (editing?.id) {
        await financialsApi.update(editing.id, payload);
        setToast({ severity: 'success', message: 'Financial entry updated' });
      } else {
        await financialsApi.create(payload);
        setToast({ severity: 'success', message: 'Financial entry created' });
      }
      setToastOpen(true);
      setEditOpen(false);
      await refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Save failed' });
      setToastOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: ProjectFinancialEntry) => {
    if (!canEdit) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Delete this financial entry (${r.type} ${money.format(r.amount)})?`);
    if (!ok) return;
    try {
      await financialsApi.remove(r.id);
      setToast({ severity: 'success', message: 'Deleted' });
      setToastOpen(true);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
      setToastOpen(true);
    }
  };

  const cols: GridColDef[] = [
    { field: 'project', headerName: 'Project', flex: 1, minWidth: 220, valueGetter: (p) => p.row?.Project?.name || p.row?.projectId },
    { field: 'type', headerName: 'Type', width: 130 },
    { field: 'amount', headerName: 'Amount', width: 140, valueGetter: (p) => money.format(Number(p.row?.amount || 0)) },
    { field: 'fiscal', headerName: 'Period', width: 120, valueGetter: (p) => (p.row?.fiscalYear ? `${p.row.fiscalYear} Q${p.row.fiscalQuarter || '?'}` : '—') },
    { field: 'transactionDate', headerName: 'Date', width: 120, valueGetter: (p) => (p.row?.transactionDate ? String(p.row.transactionDate).slice(0, 10) : '—') },
    { field: 'category', headerName: 'Category', width: 140, valueGetter: (p) => p.row?.category || '—' },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 240, valueGetter: (p) => p.row?.description || '—' },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: 'Actions',
            width: 160,
            sortable: false,
            filterable: false,
            renderCell: (p) => (
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => openEdit(p.row as any)} sx={{ borderRadius: 2 }}>
                  Edit
                </Button>
                <Button size="small" color="error" variant="outlined" onClick={() => remove(p.row as any)} sx={{ borderRadius: 2 }}>
                  Delete
                </Button>
              </Stack>
            ),
          } as GridColDef,
        ] as GridColDef[])
      : []),
  ];

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Financials"
        subtitle="Budget vs. actuals with an allocation-based forecast to completion (ETC / EAC)."
        actions={
          canEdit ? (
            <Button variant="contained" onClick={openCreate}>
              Add entry
            </Button>
          ) : null
        }
      />

      {/* Budget model: Budget − Spent = Variance, with allocation-based forecast (ETC / EAC). */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Budget" value={formatCurrencyShort(finance.budget)} subtitle="Approved ceiling" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Allocated" value={formatCurrencyShort(finance.allocated)} subtitle="Committed" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Spent"
            value={formatCurrencyShort(finance.spent)}
            subtitle={`${formatPercent(finance.utilizationPct)} of budget`}
            onClick={() => { setShowAllPanel(false); setTopPanel((p) => (p === 'spend' ? null : 'spend')); }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="ETC" value={formatCurrencyShort(finance.etc)} subtitle="Est. to complete" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="EAC" value={formatCurrencyShort(finance.eac)} subtitle="Est. at completion" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Variance"
            value={formatSignedCurrencyShort(finance.variance)}
            subtitle={finance.varianceLabel}
            tone={finance.tone}
            accent={finance.tone === 'unfavorable' ? tokens.color.danger : finance.tone === 'favorable' ? tokens.color.success : undefined}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <TableCard title="Budget vs. actuals" subtitle="Budget − EAC = Variance · Favorable means under budget">
            <NexusBarChart
              data={budgetChartData}
              xKey="name"
              series={[{ key: 'value', name: 'Amount', color: tokens.color.accent }]}
              valueFormatter={formatCurrencyShort}
              height={240}
            />
          </TableCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={6}>
              <StatCard
                title="Entries"
                value={stats.entries}
                subtitle={stats.latestDate ? `Latest ${stats.latestDate.toISOString().slice(0, 10)}` : 'No entries yet'}
                onClick={() => { setShowAllPanel(false); setTopPanel((p) => (p === 'entries' ? null : 'entries')); }}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <StatCard
                title="Revenue"
                value={formatCurrencyShort(stats.revenue)}
                subtitle="Revenue entries"
                onClick={() => { setShowAllPanel(false); setTopPanel((p) => (p === 'revenue' ? null : 'revenue')); }}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <StatCard
                title="Projects"
                value={stats.projectsCount}
                subtitle="With activity"
                onClick={() => { setShowAllPanel(false); setTopPanel((p) => (p === 'projects' ? null : 'projects')); }}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <StatCard title="Net" value={formatSignedCurrencyShort(stats.revenue - stats.spend)} subtitle="Revenue − spend" tone={stats.revenue - stats.spend >= 0 ? 'favorable' : 'unfavorable'} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Collapse in={!!topPanel} unmountOnExit>
        <TableCard
          title={topPanel === 'entries' ? 'Financial summary' : topPanel === 'spend' ? 'Top spend (by project)' : topPanel === 'revenue' ? 'Top revenue (by project)' : 'Projects with entries'}
          actions={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                {showAllPanel ? 'Show less' : 'Show all'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                Close
              </Button>
            </Stack>
          }
        >
          {topProjectsBySpend.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No entries match your current filters.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {(topPanel === 'revenue'
                ? topProjectsBySpend.slice().sort((a, b) => b.revenue - a.revenue)
                : topProjectsBySpend
              )
                .filter((p) => (topPanel === 'revenue' ? p.revenue > 0 : topPanel === 'spend' ? p.spend > 0 : true))
                .slice(0, showAllPanel ? 1000 : 5)
                .map((p) => (
                  <Box
                    key={p.projectId}
                    sx={{ p: 1, borderRadius: 2, border: '1px solid #E5E7EB', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                    onClick={() => setFilters((f) => ({ ...f, projectId: p.projectId }))}
                  >
                    <Typography sx={{ fontWeight: 700 }} noWrap>
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      spend {moneyFull.format(p.spend)} • revenue {moneyFull.format(p.revenue)} • {p.entries} entries {p.lastDate ? `• latest ${p.lastDate}` : ''}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          )}
        </TableCard>
      </Collapse>

      {!canEdit ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          You don't have edit permissions for financial entries.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ mb: 2 }}>
        <TableCard title="Filters">
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Project"
                value={filters.projectId}
                onChange={(e) => setFilters((f) => ({ ...f, projectId: e.target.value }))}
                select
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Fiscal year"
                value={filters.fiscalYear}
                onChange={(e) => setFilters((f) => ({ ...f, fiscalYear: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Quarter"
                value={filters.fiscalQuarter}
                onChange={(e) => setFilters((f) => ({ ...f, fiscalQuarter: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Type" value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} select fullWidth size="small">
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </TableCard>
      </Box>

      <TableCard title="Entries" subtitle={`${rows.length} loaded`}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <div style={{ width: '100%', height: 620 }}>
            <ResizableDataGrid
              storageKey="gridWidths.financials.entries"
              enableDensityControls
              rows={rows}
              columns={cols}
              getRowId={(r) => r.id}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            />
          </div>
        )}
      </TableCard>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit financial entry' : 'New financial entry'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField select label="Project" value={form.projectId} onChange={(e) => setForm((v) => ({ ...v, projectId: e.target.value }))} fullWidth>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Type" value={form.type} onChange={(e) => setForm((v) => ({ ...v, type: e.target.value as any }))} fullWidth>
                {TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Amount" value={form.amount} onChange={(e) => setForm((v) => ({ ...v, amount: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Fiscal year" value={form.fiscalYear} onChange={(e) => setForm((v) => ({ ...v, fiscalYear: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Quarter" value={form.fiscalQuarter} onChange={(e) => setForm((v) => ({ ...v, fiscalQuarter: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Currency" value={form.currency} onChange={(e) => setForm((v) => ({ ...v, currency: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="Transaction date" value={form.transactionDate} onChange={(e) => setForm((v) => ({ ...v, transactionDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Category" value={form.category} onChange={(e) => setForm((v) => ({ ...v, category: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" value={form.description} onChange={(e) => setForm((v) => ({ ...v, description: e.target.value }))} fullWidth multiline minRows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !canEdit} variant="contained" sx={{ borderRadius: 2 }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={2500} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
      </Container>
    </PageLoader>
  );
};

export default FinancialsPage;



